/* Genre chips. Square, never a pill, and the colour is not a decision the
 * component gets to make.
 *
 * Colour comes off the genre's `color_token`, which the API returns from a
 * FIXED map in hgr/colors.py: house is sky, ambient is cyan, disco is
 * magenta, dub is blue, everything else is paper. Regulars learn the code, and
 * a hash-to-colour scheme would reshuffle that vocabulary every time somebody
 * adds a genre.
 *
 * Selection is NOT the fill. A selected chip adds an inset rule, so the state
 * survives greyscale, colour-blindness and a photocopier. Hover INVERTS to an
 * ink field — tints are not the vocabulary anywhere in this system.
 *
 * The element follows the behaviour, never the other way round: a chip that
 * navigates is an <a>, a chip that toggles a filter is a <button>, and a chip
 * that is only a label is a <span>. There is no div with an onClick here.
 */

import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import type { ColorToken, GenreRef } from '../types';

/** The five tokens a chip may wear plus the two dark fields the kit reserves. */
type ChipTone = ColorToken | 'pick';

export interface ChipProps {
  children: ReactNode;
  /** The genre's own token. Omitted means paper, which is the "unmapped" chip. */
  color?: ChipTone | null;
  /**
   * Adds the inset rule. On a <button> this is also `aria-pressed`; on an <a>
   * it is `aria-current`, because a link is not a toggle and cannot be pressed.
   */
  selected?: boolean;
  /** In-app route. Makes it a react-router <Link>. */
  to?: string;
  /** External URL. Makes it a plain <a>. */
  href?: string;
  /** Makes it a <button>. */
  onPress?: () => void;
  /** A count drawn after the label, e.g. `HOUSE 41`. */
  count?: number | null;
  disabled?: boolean;
  className?: string;
  /** Overrides the label read aloud, for a chip whose text is an abbreviation. */
  ariaLabel?: string;
}

function toneClass(color: ChipTone | null | undefined): string {
  if (!color || color === 'paper') return '';
  return ` chip--${color}`;
}

export function Chip({
  children,
  color,
  selected = false,
  to,
  href,
  onPress,
  count,
  disabled = false,
  className,
  ariaLabel,
}: ChipProps) {
  const classes = `chip${toneClass(color)}${className ? ` ${className}` : ''}`;

  const label = (
    <>
      {children}
      {typeof count === 'number' ? <span className="chip__count">{count}</span> : null}
    </>
  );

  if (onPress) {
    return (
      <button
        type="button"
        className={classes}
        aria-pressed={selected}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={onPress}
      >
        {label}
      </button>
    );
  }

  if (to) {
    return (
      <Link
        className={classes}
        to={to}
        aria-current={selected ? 'true' : undefined}
        aria-label={ariaLabel}
      >
        {label}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        className={classes}
        href={href}
        aria-current={selected ? 'true' : undefined}
        aria-label={ariaLabel}
        rel="noreferrer"
      >
        {label}
      </a>
    );
  }

  return (
    <span
      className={selected ? `${classes} chip--sel` : classes}
      aria-label={ariaLabel}
      data-selected={selected ? 'true' : undefined}
    >
      {label}
    </span>
  );
}

export interface ChipListProps {
  /** The genres to draw. Leave it out and `children` is used instead. */
  genres?: readonly GenreRef[] | null;
  /** Route per genre. Given, the chips are links; omitted, they are labels. */
  hrefFor?: (genre: GenreRef) => string;
  /** Toggle per genre. Given, the chips are buttons and beat `hrefFor`. */
  onToggle?: (genre: GenreRef) => void;
  isSelected?: (genre: GenreRef) => boolean;
  countFor?: (genre: GenreRef) => number | null | undefined;
  /**
   * Draw at most this many and finish with a `+N` chip. The archive's genre
   * row does this at 11; a card never needs it.
   */
  max?: number;
  /** Where the `+N` chip goes. Without one it is a plain label. */
  moreTo?: string;
  /** Names the group for a screen reader, e.g. "Genres" or "Filters". */
  label?: string;
  className?: string;
  children?: ReactNode;
}

export function ChipList({
  genres,
  hrefFor,
  onToggle,
  isSelected,
  countFor,
  max,
  moreTo,
  label,
  className,
  children,
}: ChipListProps) {
  const classes = className ? `chips ${className}` : 'chips';

  if (!genres) {
    return (
      <div className={classes} role={label ? 'group' : undefined} aria-label={label}>
        {children}
      </div>
    );
  }

  const all = genres.filter(Boolean);
  const shown = typeof max === 'number' && all.length > max ? all.slice(0, max) : all;
  const hidden = all.length - shown.length;

  if (all.length === 0 && !children) return null;

  return (
    <div className={classes} role={label ? 'group' : undefined} aria-label={label}>
      {shown.map((genre) => {
        const selected = isSelected ? isSelected(genre) : false;
        const count = countFor ? countFor(genre) : null;
        return (
          <Chip
            key={genre.slug}
            color={genre.color_token}
            selected={selected}
            count={count ?? null}
            to={!onToggle && hrefFor ? hrefFor(genre) : undefined}
            onPress={onToggle ? () => onToggle(genre) : undefined}
          >
            {genre.name}
          </Chip>
        );
      })}
      {hidden > 0 ? (
        <Chip to={moreTo} ariaLabel={`${hidden} more ${hidden === 1 ? "genre" : "genres"}`}>
          +{hidden}
        </Chip>
      ) : null}
      {children}
    </div>
  );
}
